import PatientDetailsField from "../../../experience/careprovider/visits/visitdetails/components/patientdetailscomponent/PatientDetailsField";
import { getDateOnly, getUserAge } from "../../../utils/userdatautils";
import Initials from "../generateinitials/Initials";
import InfoCardComponent from "../infocard/InfoCardComponent";
import {
  careGiverFields,
  emergencyContactFields,
  facilityFields,
  patientMetaData,
  patientPreferenceFields,
  workforcePreferenceFields,
  userDetailsHeaderText,
} from "./utils";
import { getDateString } from "../../../utils/dateutils";
import TruncatedListWithPopover from "../../../experience/carecoordinator/careplan/TruncatedListWithPopover";
import TruncatedText from "../../../experience/carecoordinator/patient/TruncatedText";
import { getFullName } from "../../../experience/careprovider/visits/utils";
import LocalStorageHandler from "../../../service/memory/handler/LocalStorageHandler";
import MemoryType from "../../../service/memory/enum/MemoryType";
import PatientContactSection from "../../../experience/careprovider/visits/visitdetails/components/patientdetailscomponent/PatientContactSection";
import VolunteerProfileAction from "../../../experience/organizationsettings/profilepages/VolunteerProfileAction";

const localStorage = new LocalStorageHandler();

const PREF_SINGLE_SELECT_KEYS = new Set([
  "petComfort",
  "smokingTolerance",
  "communicationStyle",
  "energyLevel",
  "petManagement",
  "smokingStatus",
  "othersPresent",
]);

const PATIENT_DATE_KEYS = new Set(["admitDate", "certificateEndDate"]);
const WORKFORCE_DATE_KEYS = new Set(["joiningDate", "anniversary"]);
const JSON_ARRAY_DETAIL_KEYS = new Set(["diagnosis", "attributes", "preferences"]);

function parseJsonObject(value) {
  try {
    return JSON.parse(value || "{}");
  } catch {
    return {};
  }
}

function parseJsonArrayText(data) {
  try {
    const parsed = JSON.parse(data || "[]");
    return Array.isArray(parsed) ? parsed.join(", ") || null : null;
  } catch {
    return null;
  }
}

function mapStoredValueToLabels(stored, optionList, singleSelect) {
  if (!optionList?.length) return stored;
  if (singleSelect) {
    if (stored == null || stored === "") return stored;
    const match = optionList.find((o) => o.value === stored);
    return match?.label ?? stored;
  }
  if (!Array.isArray(stored) || stored.length === 0) return stored;
  return stored.map((v) => {
    const match = optionList.find((o) => o.value === v);
    return match?.label ?? v;
  });
}

/** Turns preference codes into display labels using configured option catalogs. */
export function resolvePreferencesForDisplay(
  preferences,
  optionsCatalog,
  preferenceFields,
) {
  if (!preferences || !Object.keys(preferences).length || !optionsCatalog) {
    return null;
  }
  const next = { ...preferences };
  for (const { key } of preferenceFields) {
    const opts = optionsCatalog[key];
    if (!opts?.length) continue;
    next[key] = mapStoredValueToLabels(
      preferences[key],
      opts,
      PREF_SINGLE_SELECT_KEYS.has(key),
    );
  }
  return next;
}

/** True when PatientContactSection would render at least one field. */
function hasContactSectionData(contact, fields) {
  if (!contact) return false;
  return fields.some(({ key, isFullName }) => {
    const value = isFullName
      ? getFullName(contact?.givenName, contact?.familyName)
      : contact?.[key];
    if (Array.isArray(value)) return value.length > 0;
    return value != null && value !== "";
  });
}

function formatGenderLabel(gender) {
  if (!gender) return null;
  return `${gender.charAt(0)}${gender.slice(1).toLowerCase()} `;
}

function buildAdditionalPrimaryData({ gender, birthDate }) {
  const age = birthDate && getUserAge(birthDate);
  const genderLabel = formatGenderLabel(gender);
  if (!genderLabel && (!age || Number.isNaN(age))) return null;

  return (
    <div className="additional-primary-data">
      {genderLabel && <span>{genderLabel}</span>}
      {age && !Number.isNaN(age) ? <span>Age {age}</span> : null}
    </div>
  );
}

function mapDetailFieldValue(item, entity, { dateKeys, birthDateFormatted }) {
  if (dateKeys.has(item)) {
    return entity?.[item] ? getDateString(entity[item]) : null;
  }
  if (item === "birthDate") {
    return birthDateFormatted;
  }
  if (JSON_ARRAY_DETAIL_KEYS.has(item)) {
    return parseJsonArrayText(entity?.[item]);
  }
  return entity?.[item];
}

function mapUserDetailRows(entity, { excludeKeys = [], dateKeys }) {
  const birthDateFormatted = getDateOnly(entity?.birthDate);
  return Object.keys(userDetailsHeaderText)
    .filter((key) => !excludeKeys.includes(key))
    .map((key) => ({
      label: userDetailsHeaderText[key],
      value: mapDetailFieldValue(key, entity, { dateKeys, birthDateFormatted }),
    }));
}

function mapPatientMetadataRows(metadata) {
  return Object.keys(patientMetaData).map((key) => {
    const value = metadata?.[key];
    return {
      label: patientMetaData[key],
      value:
        typeof value === "string"
          ? value
          : Object.keys(value || {})
              .filter((k) => value[k])
              .map((k) => k.replace(/_/g, " "))
              .join(", "),
    };
  });
}

function renderDetailRow(field, { truncate = false } = {}) {
  if (!field?.value) return null;
  const info = truncate ? (
    <TruncatedText text={field.value} />
  ) : (
    field.value
  );
  return (
    <PatientDetailsField key={field.label} header={field.label} info={info} />
  );
}

function buildTeamsField(teams, label) {
  if (!teams?.length) return null;
  return (
    <PatientDetailsField
      header={label}
      info={
        <TruncatedListWithPopover
          dataList={teams.map((item) => item?.name)}
          dataToBeDisplayed={2}
          direction="horizontal"
        />
      }
    />
  );
}

function buildWorkforceAvatarBlock(
  person,
  fullName,
  { showDelete = false, showUpload = false, showEdit = false } = {},
) {
  const actionProps = { userDetails: person, showDelete, showUpload, showEdit };
  return (
    <div className="initial-avatar">
      <InfoCardComponent
        size={64}
        icon={person?.icon}
        avatar={!person?.icon ? <Initials value={fullName} /> : undefined}
      />
      <VolunteerProfileAction {...actionProps} />
    </div>
  );
}

function pairSectionColProps(hasSibling) {
  return { xs: 24, lg: hasSibling ? 12 : 24 };
}

function mapPatientToProfileData(
  patient,
  patientStatusOptions,
  label,
  patientPreferenceOptions = null,
) {
  const metadata = parseJsonObject(patient?.metadata);
  const fullName = getFullName(patient?.givenName, patient?.familyName);
  const preferredName = metadata?.preferredName;
  const status =
    patientStatusOptions?.find((item) => item?.value === patient?.patientStatus)
      ?.label || "";
  const preferences = resolvePreferencesForDisplay(
    parseJsonObject(patient?.patientPreference),
    patientPreferenceOptions,
    patientPreferenceFields,
  );

  const userDetails = mapUserDetailRows(patient, {
    excludeKeys: ["completeAddress"],
    dateKeys: PATIENT_DATE_KEYS,
  });
  const userMetadata = mapPatientMetadataRows(metadata);

  const facility = {
    facilityName: patient?.facilityName,
    facilityPhone: patient?.facilityPhone,
    facilityTypeDescription: patient?.facilityTypeDescription,
  };

  const isFacilityLocation = patient?.serviceLocationType === "facility";
  const roomOrApartment =
    (isFacilityLocation && metadata?.room) ||
    (!isFacilityLocation && metadata?.apartmentNumber);

  const additionalPrimaryData = buildAdditionalPrimaryData({
    gender: patient?.gender,
    birthDate: patient?.birthDate,
  });

  const clinicalDetailsBlock = (
    <div className="profile-description-section">
      {status && (
        <PatientDetailsField header="Patient Status" info={status} />
      )}
      {userDetails.map((field) => renderDetailRow(field, { truncate: true }))}
      {roomOrApartment && (
        <PatientDetailsField
          header={isFacilityLocation ? "Room Number" : "Apartment Number"}
          info={
            <TruncatedText
              text={isFacilityLocation ? metadata?.room : metadata.apartmentNumber}
            />
          }
        />
      )}
      {(patient?.facilityCompleteAddress || patient?.completeAddress) && (
        <PatientDetailsField
          header="Service Address"
          info={
            <TruncatedText
              text={
                isFacilityLocation
                  ? patient.facilityCompleteAddress
                  : patient.completeAddress
              }
            />
          }
        />
      )}
      {buildTeamsField(patient?.teams, label)}
      {userMetadata.map((field) => renderDetailRow(field, { truncate: true }))}
      {patient?.comments && (
        <PatientDetailsField
          header="Notes"
          info={<TruncatedText text={patient.comments} />}
        />
      )}
    </div>
  );

  const preferenceSectionBlock = (
    <PatientContactSection contact={preferences} fields={patientPreferenceFields} />
  );
  const caregiverSectionBlock = (
    <PatientContactSection
      contact={metadata?.careGiver}
      fields={careGiverFields}
    />
  );
  const facilitySectionBlock = (
    <PatientContactSection contact={facility} fields={facilityFields} />
  );

  const hasFacilityData = hasContactSectionData(facility, facilityFields);
  const hasCaregiverData = hasContactSectionData(metadata?.careGiver, careGiverFields);

  const sections = [
    {
      id: "clinical",
      title: "Profile Details",
      subtitle: null,
      colProps: { xs: 24 },
      content: clinicalDetailsBlock,
    },
    preferences && {
      id: "preferences",
      title: "Preferences",
      subtitle: null,
      colProps: { xs: 24 },
      content: preferenceSectionBlock,
    },
    hasCaregiverData && {
      id: "caregiver",
      title: "Primary Caregiver / Family Data",
      subtitle: null,
      colProps: pairSectionColProps(hasFacilityData),
      content: caregiverSectionBlock,
    },
    hasFacilityData && {
      id: "facility",
      title: "Facility",
      subtitle: null,
      colProps: pairSectionColProps(hasCaregiverData),
      content: facilitySectionBlock,
    },
  ].filter(Boolean);

  return {
    primaryHeading: fullName,
    additionalInfo: patient?.medicalRecordNumber ? (
      <span>MRN: {patient.medicalRecordNumber}</span>
    ) : null,
    additionalPrimaryData,
    description: (
      <>
        {clinicalDetailsBlock}
        {preferences && preferenceSectionBlock}
        {hasCaregiverData && caregiverSectionBlock}
        {hasFacilityData && facilitySectionBlock}
      </>
    ),
    overviewConfig: {
      profileKind: "patient",
      displayName: fullName,
      preferredName,
      roleLabel: "Patient",
      medicalRecordNumber: patient?.medicalRecordNumber,
      statusLabel: status || null,
      additionalPrimaryData,
      sections,
    },
  };
}

function mapWorkforceToProfileData(
  workforce,
  label,
  showDelete = true,
  showUpload = true,
  volunteerPreferenceOptions = null,
) {
  const user = localStorage.read(MemoryType.USER);
  const metadata = parseJsonObject(workforce?.metadata);
  const fullName = getFullName(workforce?.givenName, workforce?.familyName);
  const preferences = resolvePreferencesForDisplay(
    parseJsonObject(workforce?.workforcePreference),
    volunteerPreferenceOptions,
    workforcePreferenceFields,
  );
  const isVolunteerCoordinator = user?.jobTitle === "Volunteer Coordinator";

  const userDetails = mapUserDetailRows(workforce, { dateKeys: WORKFORCE_DATE_KEYS });
  const avatarBlock = buildWorkforceAvatarBlock(workforce, fullName, {
    showDelete,
    showUpload,
  });
  const additionalPrimaryData = buildAdditionalPrimaryData({
    gender: workforce?.gender,
    birthDate: workforce?.birthDate,
  });

  const workforceMainBlock = (
    <div className="profile-description-section">
      {userDetails.map((field) => renderDetailRow(field))}
      {isVolunteerCoordinator && buildTeamsField(workforce?.teams, label)}
      {isVolunteerCoordinator && workforce?.comments && (
        <PatientDetailsField
          header="Notes"
          info={<TruncatedText text={workforce.comments} />}
        />
      )}
    </div>
  );

  const workforcePreferenceBlock = (
    <PatientContactSection contact={preferences} fields={workforcePreferenceFields} />
  );
  const workforceEmergencyBlock = (
    <PatientContactSection
      contact={metadata?.emergencyContact}
      fields={emergencyContactFields}
    />
  );

  const hasEmergencyData = hasContactSectionData(
    metadata?.emergencyContact,
    emergencyContactFields,
  );

  const sections = [
    {
      id: "workforce-main",
      title: "Profile Details",
      subtitle: null,
      colProps: { xs: 24 },
      content: workforceMainBlock,
    },
    preferences && {
      id: "workforce-preferences",
      title: "Preferences",
      subtitle: null,
      colProps: { xs: 24 },
      content: workforcePreferenceBlock,
    },
    hasEmergencyData && {
      id: "workforce-emergency",
      title: "Emergency Contact",
      subtitle: null,
      colProps: { xs: 24 },
      content: workforceEmergencyBlock,
    },
  ].filter(Boolean);

  return {
    avatar: avatarBlock,
    primaryHeading: fullName,
    additionalInfo: workforce?.memberTitle ? (
      <span>{workforce.memberTitle}</span>
    ) : null,
    additionalPrimaryData,
    description: (
      <>
        {workforceMainBlock}
        {preferences && workforcePreferenceBlock}
        {hasEmergencyData && workforceEmergencyBlock}
      </>
    ),
    overviewConfig: {
      profileKind: "volunteer",
      displayName: fullName,
      roleLabel: workforce?.jobTitle,
      statusLabel: workforce?.workforceStatus,
      memberTitle: workforce?.memberTitle || null,
      additionalPrimaryData,
      avatar: avatarBlock,
      sections,
    },
  };
}

function mapCareCoordinatorToProfileData(coordinator, label) {
  const metadata = parseJsonObject(coordinator?.metadata);
  const fullName = getFullName(coordinator?.givenName, coordinator?.familyName);
  const status = metadata?.workforceStatus
    ? metadata.workforceStatus
    : coordinator?.isActive
      ? "Active"
      : "Inactive";

  const userDetails = mapUserDetailRows(coordinator, { dateKeys: WORKFORCE_DATE_KEYS });
  const avatarBlock = buildWorkforceAvatarBlock(
    coordinator,
    fullName,
    coordinator?.icon ? { showDelete: true, showEdit: true } : {},
  );
  const additionalPrimaryData = buildAdditionalPrimaryData({
    gender: coordinator?.gender,
    birthDate: coordinator?.birthDate,
  });

  const coordinatorDetailsBlock = (
    <div className="profile-description-section">
      {userDetails.map((field) => renderDetailRow(field))}
      {buildTeamsField(coordinator?.teams, label)}
      {coordinator?.comments && (
        <PatientDetailsField
          header="Notes"
          info={<TruncatedText text={coordinator.comments} />}
        />
      )}
    </div>
  );

  const sections = [
    {
      id: "coordinator-main",
      title: "Profile Details",
      subtitle: null,
      colProps: { xs: 24 },
      content: coordinatorDetailsBlock,
    },
  ];

  return {
    avatar: avatarBlock,
    primaryHeading: fullName,
    additionalInfo: coordinator?.memberTitle ? (
      <span>Role: {coordinator.memberTitle}</span>
    ) : null,
    additionalPrimaryData,
    description: coordinatorDetailsBlock,
    overviewConfig: {
      profileKind: "coordinator",
      displayName: fullName,
      roleLabel: coordinator?.jobTitle,
      statusLabel: status || null,
      memberTitle: coordinator?.memberTitle || null,
      additionalPrimaryData,
      avatar: avatarBlock,
      sections,
    },
  };
}

export {
  mapPatientToProfileData,
  mapWorkforceToProfileData,
  mapCareCoordinatorToProfileData,
};
